// Dependencies
import React, { FunctionComponent, useEffect, useRef, useState, Fragment, useCallback } from "react";

// Styles
import { Container } from "./styles";

// Components
import { WebpBadge } from "components/WebpBadge";
import { WebpDropdown } from "components/WebpDropdown";
import { WebpBottomSheet } from "components/WebpBottomSheet";
import { ConditionallyRender } from "component-library/utilities/conditionally-render";

// Types
type WebpSelectProps = {
    id?: string;
    placeholder: string;
    disabled: boolean;
    badgeContent: string;
    showSearch: boolean;
    handleClose: () => void;
};

export const WebpSelect: FunctionComponent<WebpSelectProps> = ({
    id,
    placeholder,
    disabled,
    showSearch = false,
    badgeContent,
    handleClose,
    children
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const [isMobile, setIsMobile] = useState(false);
    const [isOpened, setIsOpened] = useState(false);

    const handleClick = useCallback(() => {
        setIsOpened((prevIsOpened) => !prevIsOpened);
    }, []);

    const loadDropdowOrBottomSheet = () => {
        window.innerWidth < 600 ? setIsMobile(true) : setIsMobile(false);
    };

    useEffect(() => {
        const handleResize = () => {
            loadDropdowOrBottomSheet();
        };

        window.addEventListener("resize", handleResize);

        loadDropdowOrBottomSheet();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!!isOpened && containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpened(false);
                handleClose();
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [handleClick, handleClose, isOpened]);

    return (
        <Container aria-disabled={disabled} ref={containerRef} isDisabled={disabled} isOpened={isOpened}>
            <button className="select-input" disabled={disabled} onClick={handleClick}>
                {placeholder}

                <div className="icons-container">
                    {!!badgeContent && !disabled && <WebpBadge content={badgeContent} />}

                    <span className="icon">
                        <svg width="8" height="4" viewBox="0 0 8 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M4.26886 3.89857C4.11526 4.03376 3.88855 4.03382 3.73488 3.8987L0.143348 0.740813C-0.0287187 0.589521 -0.0487057 0.323716 0.0987071 0.147121C0.246119 -0.029474 0.505109 -0.049987 0.677176 0.101305L4.00166 3.02439L7.32267 0.101441C7.49466 -0.0499382 7.75366 -0.0295568 7.90116 0.146964C8.04866 0.323484 8.0288 0.589299 7.85681 0.740678L4.26886 3.89857Z"
                                fill="currentColor"
                            />
                        </svg>
                    </span>
                </div>
            </button>

            <ConditionallyRender
                shouldRender={!!isOpened && !disabled}
                content={
                    <Fragment>
                        <ConditionallyRender
                            shouldRender={!!isMobile}
                            content={
                                <WebpBottomSheet id={id} placeholder={placeholder} handleOutsideClick={() => setIsOpened(false)}>
                                    {children}
                                </WebpBottomSheet>
                            }
                        />

                        <ConditionallyRender
                            shouldRender={!isMobile}
                            content={
                                <WebpDropdown id={id} isOpened showSearch={showSearch}>
                                    {children}
                                </WebpDropdown>
                            }
                        />
                    </Fragment>
                }
            />
        </Container>
    );
};
